import type React from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Loader2, UserMinus } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Handler } from "../../types/handler";
import { handlerName } from "../../types/handler";
import { useHandlers } from "../../hooks/useHandlers";
import { HandlerBubble } from "./HandlerBubble";

/**
 * ── Handing a transport to somebody ──────────────────────────────────────────
 *
 * The bubble, pressable. On an unassigned record one press takes it — and that
 * is the whole reason the control exists in this shape rather than as a
 * dropdown. The queue is shared between twelve people; claiming an order has to
 * cost less than opening it does, or nobody claims anything and two dispatchers
 * spend the same afternoon on the same booking.
 *
 * On an assigned record the press opens the roster instead, because handing a
 * record *on* is a choice between people and taking one *up* is not.
 *
 * ── Optimistic, and honest about failing ─────────────────────────────────────
 *
 * The bubble changes before the server answers. It is one small field and the
 * press is made between phone calls; waiting on a round trip to see your own
 * initials appear makes the control feel broken on a slow connection. A refusal
 * puts the previous handler back and surfaces the server's own sentence — the
 * caller's `onError`, so the page can toast it in its own voice.
 *
 * ── It restricts nothing ─────────────────────────────────────────────────────
 *
 * Any dispatcher may hand any record to anyone, themselves included, and may
 * take one off a colleague. There is no lead, no owner and no approval: this is
 * a label on shared work, and a label nobody but its subject could change would
 * be stale the first time somebody went on holiday.
 */

interface HandlerAssignProps {
  /** The handler as it stands. */
  handler?: Handler | null;
  /**
   * Persist the change. Resolve to commit; throw to roll back.
   *
   * `null` means unassign. The control does not care which endpoint this is —
   * an intake order, a container order and a container all behave identically
   * from here.
   */
  onAssign: (handlerId: string | null) => Promise<unknown>;
  /** The server's refusal, for the page to put in front of the user. */
  onError?: (message: string) => void;
  /** Turned off on records a page is already rendering read-only. */
  disabled?: boolean;
  size?: "sm" | "md";
  /** Accessible name for the button — "Handler for CO0003", and the like. */
  label?: string;
  className?: string;
}

const MENU_WIDTH = 248;
const MENU_GAP = 6;
const VIEWPORT_MARGIN = 8;

export const HandlerAssign: React.FC<HandlerAssignProps> = ({
  handler,
  onAssign,
  onError,
  disabled = false,
  size = "md",
  label,
  className = "",
}) => {
  const { t } = useTranslation("containerOrders");
  const { handlers, meId, ready } = useHandlers();

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  /**
   * What the bubble shows, while a save is in flight.
   *
   * `undefined` means "whatever the record says" — the normal state. A value
   * here is the optimistic one, held only until the save settles, so a
   * successful write hands the field straight back to the prop rather than
   * leaving the row with two sources of truth.
   */
  const [optimistic, setOptimistic] = useState<Handler | null | undefined>();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );

  const shown = optimistic === undefined ? (handler ?? null) : optimistic;

  // ── Positioning ───────────────────────────────────────────────────────────
  //
  // Portalled and measured, like `ActionMenu` beside it in the same rows: a
  // menu rendered inside a table cell is clipped by the table's own
  // `overflow-x-auto`, which is how a row near the right edge ends up with a
  // picker that cannot be read.
  useLayoutEffect(() => {
    if (!open) return;

    const place = () => {
      const button = buttonRef.current;
      if (!button) return;

      const rect = button.getBoundingClientRect();
      const left = Math.min(
        Math.max(VIEWPORT_MARGIN, rect.left),
        window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN,
      );

      // Flip above when there is not room below — the roster is twelve rows and
      // a menu that opens off the bottom of a long table is unreachable.
      const estimated = Math.min(320, 44 * (handlers.length + 1) + 16);
      const below = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
      const top =
        below < estimated && rect.top > below
          ? rect.top - estimated - MENU_GAP
          : rect.bottom + MENU_GAP;

      setCoords({ top, left });
    };

    place();
    window.addEventListener("resize", place);
    // `true` catches scrolls in any ancestor: these rows live in scrolling
    // containers, and the button moves with them.
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, handlers.length]);

  useLayoutEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !menuRef.current?.contains(target) &&
        !buttonRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // ── Writing ───────────────────────────────────────────────────────────────

  const assign = async (next: Handler | null) => {
    setOpen(false);
    if (saving) return;

    const previous = shown;
    // Pressing the person who already has it is not a write.
    if ((previous?._id ?? null) === (next?._id ?? null)) return;

    setOptimistic(next);
    setSaving(true);
    try {
      await onAssign(next?._id ?? null);
      /**
       * Hand the field back to the prop.
       *
       * The parent refetches or patches its own copy on success, and holding
       * the optimistic value past that point would make this control the
       * authority on a field it does not own — so a later change made anywhere
       * else would not show here.
       */
      setOptimistic(undefined);
    } catch (error) {
      setOptimistic(previous);
      // Cleared on the next render anyway; reset so a retry is not blocked by a
      // stale optimistic value if the parent never re-renders.
      setOptimistic(undefined);
      const message =
        typeof error === "string"
          ? error
          : ((error as { message?: string })?.message ??
            t("handler.saveFailed"));
      onError?.(message);
    } finally {
      setSaving(false);
    }
  };

  /** The one-press claim: an unassigned record becomes yours. */
  const claim = () => {
    const me = handlers.find((candidate) => candidate._id === meId);
    // No roster yet (or somehow not on it) — fall back to the menu rather than
    // writing an id the bubble then cannot render.
    if (!me) {
      setOpen(true);
      return;
    }
    void assign(me);
  };

  const press = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (disabled || saving) return;
    if (!shown && meId) claim();
    else setOpen((wasOpen) => !wasOpen);
  };

  const accessibleName =
    label ??
    (shown
      ? t("handler.changeFor", { name: handlerName(shown) })
      : t("handler.takeIt"));

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={press}
        disabled={disabled || !ready}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={accessibleName}
        title={
          shown
            ? handlerName(shown)
            : disabled
              ? t("handler.unassigned")
              : t("handler.takeIt")
        }
        className={`inline-flex items-center rounded-full transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 ${
          disabled ? "cursor-default opacity-60" : "cursor-pointer"
        } ${className}`}
      >
        {saving ? (
          <Loader2
            className={`animate-spin text-mist ${
              size === "sm" ? "h-5 w-5" : "h-6 w-6"
            }`}
          />
        ) : (
          <HandlerBubble
            handler={shown}
            meId={meId}
            size={size}
            claimable={!disabled}
            silent
          />
        )}
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              top: coords.top,
              left: coords.left,
              width: MENU_WIDTH,
            }}
            className="fixed z-70 max-h-80 overflow-y-auto rounded-lg border border-[rgba(11,15,14,0.15)] bg-white p-1 shadow-xl"
          >
            {handlers.map((candidate) => {
              const active = candidate._id === shown?._id;
              return (
                <button
                  key={candidate._id}
                  type="button"
                  role="menuitem"
                  onClick={() => void assign(candidate)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-bone"
                >
                  <HandlerBubble
                    handler={candidate}
                    meId={meId}
                    size="sm"
                    silent
                  />
                  <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-gray-700">
                    {handlerName(candidate)}
                  </span>
                  {candidate._id === meId && (
                    <span className="shrink-0 text-[10px] text-mist">
                      {t("handler.you")}
                    </span>
                  )}
                  {active && (
                    <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                  )}
                </button>
              );
            })}

            {/* Divided off, because it is the one item that takes something
                away rather than hands it somewhere. Hidden when there is
                nothing to clear. */}
            {!!shown && (
              <button
                type="button"
                role="menuitem"
                onClick={() => void assign(null)}
                className="mt-1 flex w-full items-center gap-2 border-t border-[rgba(11,15,14,0.08)] px-2 pb-1.5 pt-2 text-left transition-colors hover:bg-bone"
              >
                <UserMinus className="h-3.5 w-3.5 shrink-0 text-mist" />
                <span className="text-[12px] font-medium text-gray-700">
                  {t("handler.unassign")}
                </span>
              </button>
            )}

            {!handlers.length && (
              <p className="px-2 py-4 text-center text-[11px] text-mist">
                {t("handler.noneAvailable")}
              </p>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

export default HandlerAssign;
